import { startupUpdates } from "../game/startup-update";
import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { intlLocale, useLocale } from "../i18n/locale";
import { startupCopy } from "../i18n/startup-copy";
import type { BootSnapshot, createBootReadiness } from "../game/boot-readiness";
import { getGameOrientation } from "../game/screen-orientation";
import { XworenGlyph } from "./XworenGlyph";
import { canPresentStartupContent } from "../game/startup-presentation";
import "./StartupScreen.css";
import "./startup-reveal.css";
import "./startup-rift.css";
import "./startup-refinement.css";
import "./startup-citadel.css";

const subscribeNetwork = (listener: () => void) => {
  window.addEventListener("online", listener); window.addEventListener("offline", listener);
  return () => { window.removeEventListener("online", listener); window.removeEventListener("offline", listener); };
};
const getNetworkState = () => navigator.onLine;

export function StartupScreen({ progress, onRetry }: { progress?: BootSnapshot; onRetry?: () => void }) {
  const { locale } = useLocale();
  const copy = startupCopy[locale];
  const online = useSyncExternalStore(subscribeNetwork, getNetworkState, () => true);
  const update = useSyncExternalStore(startupUpdates.subscribe, startupUpdates.getSnapshot, startupUpdates.getSnapshot);
  const updating = ["checking", "downloading", "verifying", "applying"].includes(update.phase);
  const noConnection = !online || update.phase === "offline";
  const failed = Boolean(progress?.failed?.length) || update.phase === "unavailable";
  const updateLabel = locale === "tr"
    ? update.phase === "checking" ? "Güncellemeler denetleniyor…" : update.phase === "verifying" ? "Dosyalar doğrulanıyor…" : update.phase === "applying" ? "Güncelleme uygulanıyor…" : "Güncelleniyor…"
    : update.phase === "checking" ? "Checking for updates…" : update.phase === "verifying" ? "Verifying files…" : update.phase === "applying" ? "Applying update…" : "Updating…";
  const completed = progress?.settled.length ?? 0;
  const total = Math.max(1, progress?.total ?? 1);
  // Download bytes are not overall readiness: verification, activation and the
  // real interface still have to succeed even after the archive is complete.
  const fraction = Math.min(completed / total, 1);
  const formattedProgress = new Intl.NumberFormat(intlLocale[locale], { style: "percent", maximumFractionDigits: 0 }).format(fraction);
  const downloaded = new Intl.NumberFormat(intlLocale[locale], { maximumFractionDigits: 1 }).format(update.received / 1048576);
  const downloadTotal = new Intl.NumberFormat(intlLocale[locale], { maximumFractionDigits: 1 }).format(update.total / 1048576);
  return <main className="startup-screen startup-rift" data-preferred-orientation={getGameOrientation()} data-state={noConnection ? "offline" : failed ? "error" : progress?.ready ? "ready" : "loading"} aria-busy={!failed && !progress?.ready}>
    <div className="startup-screen__ambient" aria-hidden="true" />
    <section className="startup-screen__layout">
      <div className="startup-screen__stage" aria-hidden="true">
        <div className="startup-citadel__halo"><span /><span /></div>
        <div className="startup-citadel__sigils"><i>◇</i><i>✦</i><i>◇</i><i>✦</i></div>
        <div className="startup-rift__portal"><i /><i /><i /></div>
        <div className="startup-rift__rays"><i /><i /><i /></div>
        <picture className="startup-rift__guardian"><source media="(min-width: 1100px)" srcSet="/art/lobby/guardian-r6-800.webp" /><img src="/art/lobby/guardian-r6-480.webp" alt="" width="480" height="720" decoding="async" /></picture>
        <span className="startup-rift__platform" />
        <div className="startup-citadel__horizon"><i /><b>V</b><i /></div>
        <div className="startup-rift__embers"><i /><i /><i /><i /><i /><i /></div>
      </div>
      <div className="startup-screen__content">
        <div className="startup-screen__brand-lockup"><span className="startup-screen__mark" aria-hidden="true"><i /><i /><b /></span><div><span className="startup-screen__eyebrow">{copy.arena}</span><h1 className="startup-screen__brand">VARNAX</h1></div></div>
        <p className="startup-screen__tagline">{copy.preparing}</p>
        <div className="startup-screen__signature" aria-hidden="true"><span /><b>V</b><span /></div>
        <div className="startup-screen__loader">
          <div className="startup-screen__readiness" role="status" aria-live="polite"><span><i aria-hidden="true" />{noConnection ? copy.offline : failed ? copy.errorTitle : updating ? updateLabel : copy.loading}</span>{progress && !failed && <small>{formattedProgress}</small>}</div>
          <div className="startup-screen__track" {...(progress ? { role: "progressbar", "aria-label": copy.progress, "aria-valuemin": 0, "aria-valuemax": total, "aria-valuenow": completed, "aria-valuetext": formattedProgress } : { "aria-hidden": true as const })}>
            {progress ? <i style={{ transform: `scaleX(${fraction})` }} /> : <i className="startup-screen__indeterminate" />}
          </div>
          {updating && update.total > 1 && <small className="startup-screen__download" dir="ltr">{downloaded} / {downloadTotal} MB</small>}
          {progress && <div className="startup-screen__steps" aria-hidden="true">{(["interface", "orientation", "display"] as const).map((step, index) => <span key={step} data-settled={progress.settled.includes(step)} data-failed={progress.failed?.includes(step) ?? false}><i>{progress.settled.includes(step) ? "✓" : `0${index + 1}`}</i><span>{copy[step]}</span></span>)}</div>}
          {(noConnection || failed) && <div className="startup-screen__recovery"><p role="alert">{noConnection ? copy.offline : copy.errorBody}</p>{onRetry && <button type="button" onClick={onRetry}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11a8 8 0 1 0-2 6M20 4v7h-7" /></svg>{copy.retry}</button>}</div>}
        </div>
      </div>
    </section>
    <footer className="startup-screen__footer"><span><XworenGlyph className="startup-screen__xworen-mark" />XWoren</span><i />VARNAX</footer>
  </main>;
}

export function StartupBoundary({ readiness, children, onRetry }: { readiness: ReturnType<typeof createBootReadiness>; children: ReactNode; onRetry?: () => void }) {
  const progress = useSyncExternalStore(readiness.subscribe, readiness.getSnapshot, readiness.getSnapshot);
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let active = true;
    // The OS splash hands over only after React has committed the actual
    // boot UI. There is no extra full-bar presentation delay and no native
    // health acknowledgement from the loading screen.
    const frame = requestAnimationFrame(() => {
      void import("@capacitor/splash-screen").then(({ SplashScreen }) => {
        if (active) return SplashScreen.hide({ fadeOutDuration: 160 });
      }).catch(() => undefined);
    });
    return () => { active = false; cancelAnimationFrame(frame); };
  }, []);
  return canPresentStartupContent(progress) ? children : <StartupScreen progress={progress} {...(onRetry ? { onRetry } : {})} />;
}
